import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTruck, faCheck, faBan, faFileInvoiceDollar,
    faTriangleExclamation, faArrowUpRightFromSquare, faRotateLeft,
    faReceipt, faCircleCheck, faXmark,
} from '@fortawesome/free-solid-svg-icons';

import { AsyncButton, formatQty, sinceLabel, exactTime } from './stockShared';

/**
 * One purchase, as one card.
 *
 * WHY A CARD AND NOT ROWS. A supplier run buys five things at once; showing
 * five separate rows makes five copies of the same expected date, invites five
 * copies of the same invoice, and hides the fact that they arrive together. So
 * the order is the object on the board: it carries the estimate, the invoice,
 * the pictures, the Zoho expense, and one button — the van turned up with the
 * lot. A line received by mistake can be put back, which is the correction
 * that actually happens; receiving half an order line by line is not.
 *
 * ACTIONS ON THE RIGHT, like every request row above it. One column of things
 * you can do, in the same place on every card on the page.
 *
 * NO "PICTURES" BUTTON. The photographs are taken in the doorway at the moment
 * of receiving, in that dialog. A second way in, used later at a desk, is a
 * button for something nobody does.
 */
export default function OrderCard({
    order, isManager, onReceiveOrder, onUndoLine, onInvoice, onExpense, onCancelOrder,
}) {
    const lines = order.lines || [];
    const open = lines.filter((l) => l.status === 'ordered');
    const done = lines.filter((l) => l.status === 'delivered');
    const invoices = order.invoices || [];
    const photos = order.photos || [];

    /* ONE ZOHO EXPENSE PER SUPPLIER INVOICE (owner, 2026-09-12). The order is
       in the books when every invoice on it is; a file attached to another
       invoice's expense (a delivery note, page two) is covered by that one. */
    const expenses = order.expenses || [];
    const primaries = invoices.filter((d) => !d.covered_by_invoice_id);
    const inZoho = !!order.in_zoho;
    const partial = !inZoho && expenses.length > 0 && primaries.length > 1;
    const bookedTotal = expenses.reduce((sum, e) => sum + Number(e.zoho_expense_total || 0), 0);

    /* A cancelled purchase is a record, not a task. Nothing about it needs
       chasing — there is no invoice to file for something never bought, and
       nothing to book into Zoho — so it carries no warnings and no actions. */
    const cancelled = order.state === 'cancelled' || !!order.cancelled_at;

    return (
        <article className={`stk__order stk__order--${order.state}`}>
            <div className="stk__orderMain">
                <div className="stk__orderTitle">
                    <h4>
                        Order #{order.id}
                        {order.reference ? <span className="stk__orderRef">{order.reference}</span> : null}
                    </h4>
                    {/* EVERY STATE NAMED, none falling through. This used to
                        read "anything that is not received is on the way", so a
                        CANCELLED order sat in the history saying On the way with
                        nothing on it — cancelling detaches the lines back to the
                        to-order list, which is why it also showed 0 items. */}
                    <span className={`stk__pill stk__pill--${
                        cancelled ? 'off'
                            : order.state === 'received' ? 'ok'
                                : order.state === 'part-received' ? 'warn' : 'info'}`}
                    >
                        {cancelled
                            ? 'Cancelled'
                            : order.state === 'received'
                                ? 'Received'
                                : order.state === 'part-received'
                                    ? `${done.length} of ${lines.length} received`
                                    : 'On the way'}
                    </span>

                    {/* BOTH STATES ARE BADGES. The status line answers "where is
                        this?" at a glance, and "no invoice" / "not in the books"
                        are as much a part of that answer as "received" is —
                        showing only the good half made the badge row look like
                        nothing was wrong. Red because they are what has to be
                        chased before the month closes. */}
                    {/* ONE document: the chip opens it. SEVERAL: it opens the
                        list — a chip reading "Invoice (3)" that silently opened
                        only the first was read as "the invoice" (owner,
                        2026-09-08). */}
                    {isManager && !cancelled && (invoices.length === 1 ? (
                        <span className="stk__linkWrap">
                            <a
                                href={invoices[0].file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="stk__link stk__link--primary"
                                title={invoices[0].file_name || 'Open the invoice'}
                            >
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                <span className="stk__linkLabel">Invoice</span>
                            </a>
                        </span>
                    ) : invoices.length > 1 ? (
                        <span className="stk__linkWrap">
                            <button
                                type="button"
                                className="stk__link stk__link--primary"
                                onClick={onInvoice}
                                title={`${invoices.length} documents filed — open the list`}
                            >
                                <FontAwesomeIcon icon={faFileInvoiceDollar} />
                                <span className="stk__linkLabel">Invoices ({invoices.length})</span>
                            </button>
                        </span>
                    ) : (
                        <button type="button" className="stk__chipWarn" onClick={onInvoice}>
                            <FontAwesomeIcon icon={faTriangleExclamation} /> No invoice
                        </button>
                    ))}

                    {/* WHETHER THE MONEY IS IN THE BOOKS. Unrecorded spending is
                        what surfaces at month end with nobody able to say what it
                        was, so the un-booked state is the loud one. */}
                    {/* Opens the expense in Zoho Books itself — built from the
                        org it was booked into, so a sandbox record never links
                        into the live books or the other way round. */}
                    {isManager && !cancelled && !inZoho && (
                        <button type="button" className="stk__chipWarn" onClick={onExpense}>
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            {partial ? ` ${expenses.length} of ${primaries.length} invoices in Zoho` : ' Not in Zoho'}
                        </button>
                    )}

                    {/* One expense: the chip opens it in Zoho. Several (one per
                        supplier invoice): it opens the invoice list, where each
                        row links to its own expense. */}
                    {isManager && inZoho && (expenses.length === 1 ? (
                        <a
                            className="stk__chipOk"
                            href={order.zoho_expense_url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`Recorded ${exactTime(expenses[0].zoho_expense_at)}`
                                + `${expenses[0].zoho_vendor_name ? ` · ${expenses[0].zoho_vendor_name}` : ''}`
                                + ' — opens in Zoho Books'}
                        >
                            <FontAwesomeIcon icon={faCircleCheck} />
                            {order.zoho_is_sandbox ? 'In Zoho (sandbox)' : 'In Zoho'}
                            {` · AED ${bookedTotal.toFixed(2)}`}
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="stk__chipGo" />
                        </a>
                    ) : (
                        <button
                            type="button"
                            className="stk__chipOk"
                            onClick={onInvoice}
                            title={`${expenses.length} Zoho expenses — `
                                + `${expenses.map((e) => e.zoho_vendor_name).filter(Boolean).join(', ')}`}
                        >
                            <FontAwesomeIcon icon={faCircleCheck} />
                            {order.zoho_is_sandbox ? 'In Zoho (sandbox)' : 'In Zoho'}
                            {` · ${expenses.length} expenses · AED ${bookedTotal.toFixed(2)}`}
                        </button>
                    ))}
                </div>

                <p className="stk__rowMeta">
                    {isManager
                        ? <>Ordered by {order.ordered_by_name || 'a manager'}{' · '}</>
                        : <>Ordered{' '}</>}
                    <span title={exactTime(order.ordered_at)}>{sinceLabel(order.ordered_at)}</span>
                    {' · '}{lines.length} item{lines.length === 1 ? '' : 's'}
                </p>

                <p className="stk__eta">
                    <FontAwesomeIcon icon={faTruck} />
                    {order.expected_delivery
                        ? <>Expected: <strong>{order.expected_delivery}</strong></>
                        : <>No delivery estimate was given</>}
                </p>

                {cancelled && (
                    <p className="stk__cancelledNote">
                        <FontAwesomeIcon icon={faBan} />
                        <span>
                            Cancelled{order.cancelled_at ? ` ${sinceLabel(order.cancelled_at)}` : ''}
                            {order.note ? ` — ${order.note}` : ''}
                            . Anything still on the way went back to the to-order list.
                        </span>
                    </p>
                )}

                <ul className="stk__orderLines2">
                    {lines.map((l) => (
                        <li key={l.request_id} className={`stk__orderItem is-${l.status}`}>
                            <span className="stk__orderItemMain">
                                {l.quantity ? <strong>{formatQty(l.quantity)} ×</strong> : null}
                                {' '}{l.item_name}
                                <span className="stk__orderItemCat">{l.category_name}</span>
                            </span>

                            {l.status === 'delivered' ? (
                                <span className="stk__orderItemRight">
                                    <span className="stk__orderItemDone">
                                        <FontAwesomeIcon icon={faCheck} /> Received
                                        {l.delivered_at ? ` ${sinceLabel(l.delivered_at)}` : ''}
                                    </span>
                                    {/* Ticked the wrong line, or the box turned out to
                                        be somebody else's — putting it back is one
                                        click, not a support request. */}
                                    <AsyncButton
                                        className="stk__iconBtn"
                                        onClick={() => onUndoLine(l)}
                                        title="Put this back to on the way"
                                        aria-label={`Undo receiving ${l.item_name}`}
                                    >
                                        <FontAwesomeIcon icon={faRotateLeft} />
                                    </AsyncButton>
                                </span>
                            ) : l.status === 'cancelled' ? (
                                <span className="stk__orderItemDone stk__orderItemDone--off">
                                    <FontAwesomeIcon icon={faBan} /> Cancelled
                                </span>
                            ) : (
                                <span className="stk__orderItemDone stk__orderItemDone--wait">
                                    On the way
                                </span>
                            )}
                        </li>
                    ))}
                </ul>

                {photos.length > 0 && (
                    <div className="stk__strip">
                        {photos.map((p) => (
                            <a
                                key={p.id}
                                href={p.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="stk__stripShot"
                                title={`${p.uploaded_by_name || 'Added'} · ${exactTime(p.uploaded_at)}`}
                            >
                                <img src={p.file_url} alt={p.file_name || 'Delivery'} loading="lazy" />
                            </a>
                        ))}
                    </div>
                )}
            </div>

            <div className="stk__rowActions">
                {!cancelled && open.length > 0 && (
                    <button type="button" className="stk__btn stk__btn--ok" onClick={onReceiveOrder}>
                        <FontAwesomeIcon icon={faCheck} />{' '}
                        {open.length === lines.length ? 'It all arrived' : `Receive the rest (${open.length})`}
                    </button>
                )}

                {isManager && !cancelled && (
                    <>
                        {/* THE PAIR CARRIES THE WARNING. A purchase with no
                            invoice, or not in the books, is what has to be
                            chased before the month closes — so those two say so
                            in red rather than looking like ordinary actions.
                            Two per row, never three: three overflowed the
                            column and overlapped each other. */}
                        <div className="stk__rowActionsMinor">
                            <button
                                type="button"
                                className={invoices.length ? 'stk__iconBtn' : 'stk__iconBtn stk__iconBtn--warn'}
                                onClick={onInvoice}
                            >
                                <FontAwesomeIcon icon={invoices.length ? faFileInvoiceDollar : faTriangleExclamation} />
                                {invoices.length ? ` Invoice (${invoices.length})` : ' No invoice'}
                            </button>

                            {inZoho ? (expenses.length === 1 ? (
                                <a
                                    className="stk__iconBtn"
                                    href={order.zoho_expense_url || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open this expense in Zoho Books"
                                >
                                    <FontAwesomeIcon icon={faReceipt} /> In Zoho
                                </a>
                            ) : (
                                <button
                                    type="button"
                                    className="stk__iconBtn"
                                    onClick={onInvoice}
                                    title="Each supplier invoice is its own expense — open the list"
                                >
                                    <FontAwesomeIcon icon={faReceipt} /> In Zoho ({expenses.length})
                                </button>
                            )) : (
                                <button
                                    type="button"
                                    className="stk__iconBtn stk__iconBtn--warn"
                                    onClick={onExpense}
                                    title={primaries.length > 1
                                        ? 'Each supplier invoice is recorded in Zoho on its own'
                                        : 'Record this purchase in Zoho'}
                                >
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                    {partial ? ` ${expenses.length}/${primaries.length} in Zoho` : ' Not in Zoho'}
                                </button>
                            )}
                        </div>

                        {/* Un-buying is a purchasing decision, same as buying —
                            manager-only, asks for a reason, and full width so it
                            is never mistaken for one of the pair above. Hidden
                            once the money is in Zoho: that needs a credit note. */}
                        {open.length > 0 && expenses.length === 0 && (
                            <button
                                type="button"
                                className="stk__btn stk__btn--outlineDanger stk__btn--block"
                                onClick={onCancelOrder}
                                title="Cancel this order and put the items back on the to-order list"
                            >
                                <FontAwesomeIcon icon={faXmark} /> Cancel order
                            </button>
                        )}
                    </>
                                )}
            </div>
        </article>
    );
}
